<script setup lang="ts">
import { computed } from 'vue';
import LiveTrackCard from '@/components/LiveTrackCard.vue';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { readNestedId } from '@/utils/nestedRelation';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/**
 * Fixed card header for Live Track search cards.
 *
 * The cards view renders one entity-specific header per row (the
 * LocationZonePlayer status header is the other one); this is the Live Track
 * flavor, built on the shared {@link file:src/components/LiveTrackCard.vue}
 * preview surface (`flat` — the row's own Card provides the chrome) so search
 * cards read as the same track previews used by the Playlists report and the
 * audio-file-issues surfaces. Always shown regardless of Column Settings:
 * cover art (full-bleed play target), title / artist / album, genre, the
 * active-state dot with the code pinned top-right, and a bottom mono
 * bitrate / ISRC row.
 */
const props = withDefaults(
  defineProps<{
    row: Record<string, unknown>;
    /**
     * The quick-search fields this row matched (SAU-591), passed in by the
     * search page. Each is highlighted where the card draws it, and the line
     * that holds it wraps. `cardShownMatchFields` lists the same fields, and
     * the two must stay in step.
     */
    matchedFields?: readonly string[];
  }>(),
  { matchedFields: () => [] },
);

function readDisplayString(value: unknown): string | null {
  if (value && typeof value === 'object' && 'display_string' in value) {
    const display = (value as { display_string?: unknown }).display_string;
    return typeof display === 'string' && display.trim() !== '' ? display : null;
  }
  return null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}


/** Same m:ss presentation the other LiveTrackCard consumers use. */
function formatDuration(totalSeconds: number | null): string | null {
  if (totalSeconds == null || totalSeconds < 0) {
    return null;
  }
  const totalWhole = Math.round(totalSeconds);
  const minutes = Math.floor(totalWhole / 60);
  const seconds = totalWhole % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

const code = computed(() => readString(props.row.code));

const title = computed(() => {
  const name = readString(props.row.name);
  const remix = readString(props.row.remix_name);
  if (!name) return code.value ?? `#${props.row.id}`;
  return remix ? `${name} (${remix})` : name;
});

const artist = computed(() => readDisplayString(props.row.artist));
const album = computed(() => readDisplayString(props.row.album));
const artistId = computed(() => readNestedId(props.row.artist));
const albumId = computed(() => readNestedId(props.row.album));
const genre = computed(() => readDisplayString(props.row.genre));
const coverUrl = computed(() => readString(props.row.cover_art_signed_url));
const bitrate = computed(() => readNumber(props.row.bitrate));
const isrc = computed(() => readString(props.row.isrc));
const copyright = computed(() => readNumber(props.row.copyright));
const duration = computed(() => formatDuration(readNumber(props.row.duration)));

const isActive = computed(() => props.row.active === true);
const activeLabel = computed(() => (isActive.value ? 'Active' : 'Inactive'));

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
function isMatched(field: string): boolean {
  return props.matchedFields.includes(field);
}
const codeMatched = computed(() => isMatched('code'));
const titleMatched = computed(() => isMatched('name') || isMatched('remix_name') || isMatched('display_string'));

/** The matched fields LiveTrackCard draws itself. */
const cardMatched = computed(() => [
  ...(titleMatched.value ? (['title'] as const) : []),
  ...(isMatched('artist.display_string') ? (['artist'] as const) : []),
  ...(isMatched('album.display_string') ? (['album'] as const) : []),
  ...(isMatched('copyright') ? (['copyright'] as const) : []),
]);

/** PlayTrackButton props for the cover overlay (full-bleed play target on the art). */
const coverPlay = computed(() => ({
  kind: 'live_track' as const,
  liveTrackCode: code.value,
  hasAudio: Boolean(code.value),
  title: title.value,
  artist: artist.value,
  album: album.value,
  coverUrl: coverUrl.value,
  // Records for the player bar's identity lines.
  liveTrackId: readNumber(props.row.id),
  artistId: artistId.value,
  albumId: albumId.value,
}));
</script>

<template>
  <LiveTrackCard
    flat
    :stack="false"
    :data-testid="`live-track-search-card-${code ?? row.id}`"
    :cover-url="coverUrl"
    :copyright="copyright"
    :duration="duration"
    :title="title"
    :artist="artist"
    :album="album"
    title-truncate
    :title-tooltip="title"
    :cover-play="coverPlay"
    :cover-expand-testid="`live-track-search-card-expand-cover-${code ?? row.id}`"
    :matched="cardMatched"
    :wrap-title-row="titleMatched || codeMatched"
  >
    <!-- Code + active dot share the title row (no reserved column): the title
         truncates beside them and artist/album/meta flow full-width below. -->
    <template #titleTrailing>
      <!-- [flex-shrink:99]: when the row runs out of width the code clips
           (truncates) long before the title starts to — the main information
           keeps priority. The dot never clips. While the code holds a quick-search
         match it wraps instead, and so does the title row. -->
      <span class="text-muted-foreground flex min-w-0 [flex-shrink:99] items-center gap-1.5 overflow-hidden font-mono leading-none tabular-nums tracking-tight">
        <span
          v-if="code"
          class="min-w-0 text-[11px]"
          :class="codeMatched ? 'wrap-anywhere' : 'truncate'"
          :title="code"
          aria-hidden="true"
          data-testid="live-track-card-code"
        >
          <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="codeMatched">{{ code }}</SearchHighlight>
        </span>
        <Tooltip>
          <TooltipTrigger as-child>
            <span
              class="inline-block size-2 shrink-0 rounded-full"
              :class="isActive ? 'bg-green-500' : 'bg-red-500'"
              role="img"
              :aria-label="activeLabel"
              data-testid="live-track-active-dot"
              :data-active="isActive ? 'true' : 'false'"
            />
          </TooltipTrigger>
          <TooltipContent side="top" align="end">{{ activeLabel }}</TooltipContent>
        </Tooltip>
      </span>
    </template>

    <template v-if="genre" #badges>
      <div class="mt-1.5 flex flex-wrap items-center gap-2">
        <span
          class="text-muted-foreground text-xs leading-none"
          :class="isMatched('genre.display_string') ? 'wrap-anywhere' : 'truncate'"
          data-testid="live-track-card-genre"
        >
          <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('genre.display_string')">{{
            genre
          }}</SearchHighlight>
        </span>
      </div>
    </template>

    <template v-if="bitrate != null || isrc" #meta>
      <div
        class="text-muted-foreground mt-auto flex flex-wrap items-baseline gap-x-2.5 gap-y-1 pt-1.5 font-mono text-[11px] leading-none tabular-nums"
      >
        <Tooltip v-if="bitrate != null">
          <TooltipTrigger as-child>
            <span class="cursor-default whitespace-nowrap"
              ><SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('bitrate')" whole>{{
                bitrate
              }}</SearchHighlight>k</span
            >
          </TooltipTrigger>
          <TooltipContent>DB Bitrate</TooltipContent>
        </Tooltip>
        <span v-if="isrc" class="whitespace-nowrap"
          >ISRC <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="isMatched('isrc')">{{
            isrc
          }}</SearchHighlight></span
        >
      </div>
    </template>
  </LiveTrackCard>
</template>
